import { useCallback, useState } from "react";
import { getGroupId, hasMoreDataInGroups, isGroupChild, isGroupChildParent, isGroupParent, } from "./TableGroupingColumn";
import { areRowsEqual, getDataItemIndex } from "./utils";
export const getUpdatedRowSelection = (selectedIndex, selectionMode, dataItem, dataItemKey, selectedItems, selectionStatus) => {
    if (selectionMode === "multiple") {
        if (selectionStatus) {
            return selectedIndex !== -1 ? [...selectedItems] : [...selectedItems, dataItem];
        }
        else {
            return selectedIndex !== -1
                ? selectedItems.filter((item) => !areRowsEqual(item, dataItem, dataItemKey))
                : [...selectedItems];
        }
    }
    else {
        return selectionStatus ? [dataItem] : [];
    }
};
export const useTableSelection = (data, selectionMode, selected, hasMoreData, initialData, dataItemKey, grouping, onGroupingConfigChange, onSelectionChange) => {
    const [lastSelection, setLastSelection] = useState(undefined);
    const countSelectable = useCallback(() => (initialData ? initialData.length - ((grouping === null || grouping === void 0 ? void 0 : grouping.size) || 0) : 0), [initialData, grouping]);
    const getNewGroupChildSelection = (groupConfig, groupId, dataItem, selectedIndex, newSelectionStatus, items) => {
        const rowSelection = getUpdatedRowSelection(selectedIndex, selectionMode, dataItem, dataItemKey, items, newSelectionStatus);
        if (grouping && onGroupingConfigChange) {
            let newGroupConfigSelection = false;
            if (newSelectionStatus) {
                const isAsyncGroupSelected = groupConfig.hasMoreData && groupConfig.selected;
                newGroupConfigSelection = Boolean(rowSelection &&
                    data &&
                    (!groupConfig.hasMoreData || isAsyncGroupSelected) &&
                    rowSelection.filter((item) => isGroupChildParent(item, groupId)).length ===
                        data.filter((item) => isGroupChildParent(item, groupId)).length);
            }
            if (groupConfig.selected !== newGroupConfigSelection) {
                onGroupingConfigChange(new Map([...grouping, [groupId, Object.assign(Object.assign({}, groupConfig), { selected: newGroupConfigSelection })]]));
            }
        }
        return rowSelection;
    };
    const groupSelectionToggle = (groupConfig, groupId, selectedItems, newSelectionStatus) => {
        let newItems = [];
        if (newSelectionStatus === groupConfig.selected) {
            return selectedItems;
        }
        if (!newSelectionStatus) {
            // deselecting group
            newItems =
                (selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.filter((item) => {
                    return !isGroupChild(item) || !isGroupChildParent(item, groupId);
                })) || [];
        }
        else {
            // selecting group
            const items = (data === null || data === void 0 ? void 0 : data.filter((item) => {
                const selectedIndex = getDataItemIndex(selectedItems, item, dataItemKey);
                return isGroupChild(item) && isGroupChildParent(item, groupId) && selectedIndex === -1;
            })) || [];
            newItems = [...selectedItems, ...items];
        }
        if (grouping && onGroupingConfigChange) {
            groupConfig.selected = !(groupConfig === null || groupConfig === void 0 ? void 0 : groupConfig.selected);
            onGroupingConfigChange(new Map([...grouping, [groupId, groupConfig]]));
        }
        return newItems;
    };
    const getNewSelectionStatus = (dataItem, selectedIndex) => {
        let newSelectionStatus = selectedIndex === -1;
        if (grouping && isGroupParent(dataItem)) {
            const groupId = getGroupId(dataItem);
            if (groupId) {
                const groupConfig = grouping.get(groupId);
                newSelectionStatus = !(groupConfig === null || groupConfig === void 0 ? void 0 : groupConfig.selected);
            }
        }
        return newSelectionStatus;
    };
    const handleGroupRelatedSelection = (dataItem, selectedItems, isParent, selectedIndex, newSelectionStatus, shouldSelectParent) => {
        let newItems = selectedItems;
        const groupId = getGroupId(dataItem);
        if (grouping && groupId && grouping.has(groupId)) {
            if (isParent && !shouldSelectParent) {
                return newItems;
            }
            const groupConfig = grouping.get(groupId);
            if (groupConfig) {
                newItems = isParent
                    ? groupSelectionToggle(groupConfig, groupId, selectedItems, newSelectionStatus)
                    : getNewGroupChildSelection(groupConfig, groupId, dataItem, selectedIndex, newSelectionStatus, selectedItems);
            }
        }
        return newItems;
    };
    const handleRowSelection = (selectedIndex, dataItem) => {
        let newItems;
        const isParent = isGroupParent(dataItem);
        const newSelectionStatus = getNewSelectionStatus(dataItem, selectedIndex);
        if (grouping && (isParent || isGroupChild(dataItem))) {
            newItems = handleGroupRelatedSelection(dataItem, selected.items, isParent, selectedIndex, newSelectionStatus, true);
        }
        else {
            newItems = getUpdatedRowSelection(selectedIndex, selectionMode, dataItem, dataItemKey, selected.items, newSelectionStatus);
        }
        // notify consumer of updated selection
        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange({
            allSelected: selectionMode === "single"
                ? false
                : countSelectable() === newItems.length && !hasMoreData && !hasMoreDataInGroups(grouping),
            items: newItems,
        });
    };
    const batchSelect = (items, newSelectionStatus) => {
        let selectedItems = selected.items;
        items.forEach((el, index, array) => {
            const itemSelectedIndex = selectedItems.findIndex((item) => areRowsEqual(item, el, dataItemKey));
            const isParent = isGroupParent(el);
            if (grouping && (isParent || isGroupChild(el))) {
                // In batch mode, we only need to select the parent if it is the first or last element of the batch selection
                const shouldToggleParent = isParent && (index === array.length - 1 || index === 0);
                selectedItems = handleGroupRelatedSelection(el, selectedItems, isParent, itemSelectedIndex, newSelectionStatus, shouldToggleParent);
            }
            else {
                selectedItems = getUpdatedRowSelection(itemSelectedIndex, selectionMode, el, dataItemKey, selectedItems, newSelectionStatus);
            }
        });
        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange({
            allSelected: countSelectable() === selectedItems.length && !hasMoreData && !hasMoreDataInGroups(grouping),
            items: selectedItems,
        });
    };
    const getLastSelectedIndex = () => {
        return lastSelection === undefined
            ? getDataItemIndex(data || [], selected.items[0], dataItemKey)
            : getDataItemIndex(data || [], lastSelection, dataItemKey);
    };
    const handleSelection = (selectedIndex, dataItem, isShiftPressed) => {
        if (isShiftPressed && data && selectionMode === "multiple") {
            const dataItemIndex = getDataItemIndex(data, dataItem, dataItemKey);
            if (lastSelection !== undefined || selected.items.length > 0) {
                const lastSelectedIndex = getLastSelectedIndex();
                const from = lastSelectedIndex < dataItemIndex ? lastSelectedIndex : dataItemIndex;
                const to = lastSelectedIndex > dataItemIndex ? lastSelectedIndex : dataItemIndex;
                const selection = data.slice(from, to + 1);
                const newSelectionStatus = getNewSelectionStatus(dataItem, selectedIndex);
                batchSelect(selection, newSelectionStatus);
            }
            else {
                handleRowSelection(selectedIndex, dataItem);
            }
        }
        else {
            // do not allow deselect if a selection mode is single
            if (selectionMode === "single" && selectedIndex === 0) {
                return;
            }
            handleRowSelection(selectedIndex, dataItem);
        }
        setLastSelection(dataItem);
    };
    const toggleAllGroupsSelection = (value) => {
        const newConfig = new Map();
        grouping === null || grouping === void 0 ? void 0 : grouping.forEach((group, groupId) => {
            newConfig.set(groupId, Object.assign(Object.assign({}, group), { selected: value }));
        });
        if (onGroupingConfigChange) {
            onGroupingConfigChange(newConfig);
        }
    };
    const handleHeaderSelection = () => {
        const newItems = data ? data.filter((item) => !isGroupParent(item)) : [];
        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange({ items: selected.allSelected ? [] : newItems || [], allSelected: !selected.allSelected });
        toggleAllGroupsSelection(!selected.allSelected);
    };
    return {
        handleSelection,
        handleHeaderSelection,
        handleGroupRelatedSelection,
    };
};
//# sourceMappingURL=useTableSelection.js.map